import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '../../../shared/components/toast/useToast';
import {
  addonsApi,
  adminApi,
  availabilityApi,
  bookingsApi,
  intakeApi,
  ordersApi,
  packagesApi,
  portfolioApi,
  providerApi,
  servicesApi,
  waitlistApi,
} from '../api/charterApi';
import type {
  CancelPayload,
  CharterCancellationPolicy,
  CreateAddonPayload,
  CreatePackagePayload,
  CreatePortfolioWorkPayload,
  CreateServicePayload,
  OverrideDurationPayload,
  RefundDecisionPayload,
  ReschedulePayload,
  ScheduleSessionPayload,
  SetAvailabilityPayload,
  SetIntakeFormPayload,
  UpdateAddonPayload,
  UpdateGatewayStatePayload,
  UpdatePackagePayload,
  UpdatePortfolioWorkPayload,
  UpdateProviderProfilePayload,
  UpdateServicePayload,
  UpdateWaitlistEntryPayload,
} from '../types';

const errMsg = (err: unknown, fallback: string): string => {
  const msg = (err as { message?: unknown })?.message;
  if (Array.isArray(msg)) return (msg as string[])[0] || fallback;
  return (typeof msg === 'string' ? msg : '') || fallback;
};

// Root cache key; every merchant query is additionally scoped by the selected tenant.
const KEY = 'charter';
const enabledFor = (tenantId: string | null | undefined) => !!tenantId;

// ── Services ──────────────────────────────────────────────────────────────────

export const useCharterServices = (tenantId: string | null | undefined) =>
  useQuery({
    queryKey: [KEY, 'services', tenantId],
    queryFn: () => servicesApi.list(),
    enabled: enabledFor(tenantId),
    staleTime: 60 * 1000,
  });

export const useCreateCharterService = () => {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (data: CreateServicePayload) => servicesApi.create(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY, 'services'] });
      toast.success('Service created');
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to create service')),
  });
};

export const useUpdateCharterService = () => {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateServicePayload }) => servicesApi.update(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY, 'services'] });
      toast.success('Service updated');
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to update service')),
  });
};

export const useArchiveCharterService = () => {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (id: string) => servicesApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY, 'services'] });
      toast.success('Service archived');
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to archive service')),
  });
};

// ── Packages ──────────────────────────────────────────────────────────────────

export const useCharterPackages = (tenantId: string | null | undefined) =>
  useQuery({
    queryKey: [KEY, 'packages', tenantId],
    queryFn: () => packagesApi.list(),
    enabled: enabledFor(tenantId),
    staleTime: 60 * 1000,
  });

export const useCreateCharterPackage = () => {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (data: CreatePackagePayload) => packagesApi.create(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY, 'packages'] });
      toast.success('Package created');
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to create package')),
  });
};

export const useUpdateCharterPackage = () => {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdatePackagePayload }) => packagesApi.update(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY, 'packages'] });
      toast.success('Package updated');
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to update package')),
  });
};

export const useArchiveCharterPackage = () => {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (id: string) => packagesApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY, 'packages'] });
      toast.success('Package archived');
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to archive package')),
  });
};

// ── Add-ons ─────────────────────────────────────────────────────────────────

export const useCharterAddons = (tenantId: string | null | undefined) =>
  useQuery({
    queryKey: [KEY, 'addons', tenantId],
    queryFn: () => addonsApi.list(),
    enabled: enabledFor(tenantId),
    staleTime: 60 * 1000,
  });

export const useCreateCharterAddon = () => {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (data: CreateAddonPayload) => addonsApi.create(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY, 'addons'] });
      toast.success('Add-on created');
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to create add-on')),
  });
};

export const useUpdateCharterAddon = () => {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateAddonPayload }) => addonsApi.update(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY, 'addons'] });
      toast.success('Add-on updated');
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to update add-on')),
  });
};

export const useArchiveCharterAddon = () => {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (id: string) => addonsApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY, 'addons'] });
      toast.success('Add-on archived');
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to archive add-on')),
  });
};

// ── Bookings ────────────────────────────────────────────────────────────────

export const useCharterBookings = (tenantId: string | null | undefined) =>
  useQuery({
    queryKey: [KEY, 'bookings', tenantId],
    queryFn: () => bookingsApi.list(),
    enabled: enabledFor(tenantId),
    staleTime: 30 * 1000,
  });

export const useCharterBooking = (id: string | null, tenantId: string | null | undefined) =>
  useQuery({
    queryKey: [KEY, 'booking', id, tenantId],
    queryFn: () => bookingsApi.get(id as string),
    enabled: !!id && enabledFor(tenantId),
  });

export const useConfirmBooking = () => {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (id: string) => bookingsApi.confirm(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY, 'booking'] });
      qc.invalidateQueries({ queryKey: [KEY, 'bookings'] });
      toast.success('Booking confirmed');
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to confirm booking')),
  });
};

export const useCancelBooking = () => {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: CancelPayload }) => bookingsApi.cancel(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY, 'booking'] });
      qc.invalidateQueries({ queryKey: [KEY, 'bookings'] });
      toast.success('Booking cancelled');
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to cancel booking')),
  });
};

export const useRescheduleBooking = () => {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ReschedulePayload }) => bookingsApi.reschedule(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY, 'booking'] });
      qc.invalidateQueries({ queryKey: [KEY, 'bookings'] });
      toast.success('Booking rescheduled');
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to reschedule booking')),
  });
};

export const useOverrideBookingDuration = () => {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: OverrideDurationPayload }) =>
      bookingsApi.overrideDuration(id, data),
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: [KEY, 'booking'] });
      qc.invalidateQueries({ queryKey: [KEY, 'bookings'] });
      if (result.conflicts && result.conflicts.length > 0) {
        toast.error(`Saved with ${result.conflicts.length} slot conflict(s) — review below`);
      } else {
        toast.success('Duration updated');
      }
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to override duration')),
  });
};

export const useBookingBalanceLink = () => {
  const { toast } = useToast();
  return useMutation({
    mutationFn: (id: string) => bookingsApi.balanceLink(id),
    onSuccess: () => toast.success('Balance payment link generated'),
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to generate balance link')),
  });
};

// ── Orders ──────────────────────────────────────────────────────────────────

export const useCharterOrders = (tenantId: string | null | undefined) =>
  useQuery({
    queryKey: [KEY, 'orders', tenantId],
    queryFn: () => ordersApi.list(),
    enabled: enabledFor(tenantId),
    staleTime: 30 * 1000,
  });

export const useCharterOrder = (id: string | null, tenantId: string | null | undefined) =>
  useQuery({
    queryKey: [KEY, 'order', id, tenantId],
    queryFn: () => ordersApi.get(id as string),
    enabled: !!id && enabledFor(tenantId),
  });

export const useConfirmOrder = () => {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (id: string) => ordersApi.confirm(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY, 'order'] });
      qc.invalidateQueries({ queryKey: [KEY, 'orders'] });
      toast.success('Order confirmed');
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to confirm order')),
  });
};

export const useCancelOrderWithRefund = () => {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: RefundDecisionPayload }) =>
      ordersApi.cancelWithRefund(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY, 'order'] });
      qc.invalidateQueries({ queryKey: [KEY, 'orders'] });
      toast.success('Order cancelled with refund decision applied');
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to cancel order')),
  });
};

export const useOrderBalanceLink = () => {
  const { toast } = useToast();
  return useMutation({
    mutationFn: (id: string) => ordersApi.balanceLink(id),
    onSuccess: () => toast.success('Balance payment link generated'),
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to generate balance link')),
  });
};

export const useScheduleOrderSession = () => {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: ({ id, stepId, data }: { id: string; stepId: string; data: ScheduleSessionPayload }) =>
      ordersApi.scheduleSession(id, stepId, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY, 'order'] });
      qc.invalidateQueries({ queryKey: [KEY, 'orders'] });
      toast.success('Session scheduled');
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to schedule session')),
  });
};

// ── Provider profile ──────────────────────────────────────────────────────────

export const useCharterProvider = (tenantId: string | null | undefined) =>
  useQuery({
    queryKey: [KEY, 'provider', tenantId],
    queryFn: () => providerApi.getOwn(),
    enabled: enabledFor(tenantId),
    staleTime: 60 * 1000,
  });

export const useUpdateProviderProfile = () => {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (data: UpdateProviderProfilePayload) => providerApi.updateProfile(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY, 'provider'] });
      toast.success('Profile updated');
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to update profile')),
  });
};

export const useUpdateGatewayState = () => {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (data: UpdateGatewayStatePayload) => providerApi.updateGatewayState(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY, 'provider'] });
      toast.success('Gateway state updated');
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to update gateway state')),
  });
};

export const useUpdateCancellationPolicy = () => {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (data: CharterCancellationPolicy) => providerApi.updateCancellationPolicy(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY, 'provider'] });
      toast.success('Cancellation policy updated');
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to update policy')),
  });
};

// ── Availability ──────────────────────────────────────────────────────────────

export const useCharterAvailability = (tenantId: string | null | undefined) =>
  useQuery({
    queryKey: [KEY, 'availability', tenantId],
    queryFn: () => availabilityApi.get(),
    enabled: enabledFor(tenantId),
    staleTime: 60 * 1000,
  });

export const useSetAvailability = () => {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (data: SetAvailabilityPayload) => availabilityApi.setWindows(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY, 'availability'] });
      toast.success('Availability updated');
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to update availability')),
  });
};

// ── Intake form ───────────────────────────────────────────────────────────────

export const useCharterIntakeForm = (tenantId: string | null | undefined) =>
  useQuery({
    queryKey: [KEY, 'intake', tenantId],
    queryFn: () => intakeApi.getOwn(),
    enabled: enabledFor(tenantId),
    staleTime: 60 * 1000,
  });

export const useSetIntakeForm = () => {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (data: SetIntakeFormPayload) => intakeApi.setForm(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY, 'intake'] });
      toast.success('Intake form saved');
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to save intake form')),
  });
};

// ── Portfolio ─────────────────────────────────────────────────────────────────

export const useCharterPortfolio = (tenantId: string | null | undefined) =>
  useQuery({
    queryKey: [KEY, 'portfolio', tenantId],
    queryFn: () => portfolioApi.list(),
    enabled: enabledFor(tenantId),
    staleTime: 60 * 1000,
  });

export const useCreatePortfolioWork = () => {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (data: CreatePortfolioWorkPayload) => portfolioApi.create(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY, 'portfolio'] });
      toast.success('Portfolio work created');
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to create portfolio work')),
  });
};

export const useUpdatePortfolioWork = () => {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdatePortfolioWorkPayload }) =>
      portfolioApi.update(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY, 'portfolio'] });
      toast.success('Portfolio work updated');
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to update portfolio work')),
  });
};

export const useDeletePortfolioWork = () => {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (id: string) => portfolioApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY, 'portfolio'] });
      toast.success('Portfolio work deleted');
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to delete portfolio work')),
  });
};

export const useUploadPortfolioMedia = () => {
  const { toast } = useToast();
  return useMutation({
    mutationFn: (file: File) => portfolioApi.upload(file),
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to upload media')),
  });
};

// ── Waitlist ────────────────────────────────────────────────────────────────

export const useCharterWaitlist = (tenantId: string | null | undefined) =>
  useQuery({
    queryKey: [KEY, 'waitlist', tenantId],
    queryFn: () => waitlistApi.list(),
    enabled: enabledFor(tenantId),
    staleTime: 60 * 1000,
  });

export const useUpdateWaitlistEntry = () => {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateWaitlistEntryPayload }) =>
      waitlistApi.update(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY, 'waitlist'] });
      toast.success('Waitlist entry updated');
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to update waitlist entry')),
  });
};

// ── TCS Admin (cross-provider) ────────────────────────────────────────────────

export const useCharterAdminProviders = () =>
  useQuery({
    queryKey: [KEY, 'admin', 'providers'],
    queryFn: () => adminApi.listProviders(),
    staleTime: 60 * 1000,
  });

export const useCharterAdminProvider = (merchantId: string | null) =>
  useQuery({
    queryKey: [KEY, 'admin', 'provider', merchantId],
    queryFn: () => adminApi.getProvider(merchantId as string),
    enabled: !!merchantId,
  });

export const useCharterAdminProviderAddons = (merchantId: string | null) =>
  useQuery({
    queryKey: [KEY, 'admin', 'provider-addons', merchantId],
    queryFn: () => adminApi.listProviderAddons(merchantId as string),
    enabled: !!merchantId,
    staleTime: 60 * 1000,
  });

export const useCreateAdminProviderAddon = () => {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: ({ merchantId, data }: { merchantId: string; data: CreateAddonPayload }) =>
      adminApi.createProviderAddon(merchantId, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY, 'admin', 'provider-addons'] });
      toast.success('Add-on created for provider');
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to create add-on')),
  });
};

export const useUpdateAdminProviderAddon = () => {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: ({ merchantId, id, data }: { merchantId: string; id: string; data: UpdateAddonPayload }) =>
      adminApi.updateProviderAddon(merchantId, id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY, 'admin', 'provider-addons'] });
      toast.success('Add-on updated');
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to update add-on')),
  });
};

export const useCharterAdminBookings = () =>
  useQuery({
    queryKey: [KEY, 'admin', 'bookings'],
    queryFn: () => adminApi.listBookings(),
    staleTime: 30 * 1000,
  });

export const useCharterAdminOrders = () =>
  useQuery({
    queryKey: [KEY, 'admin', 'orders'],
    queryFn: () => adminApi.listOrders(),
    staleTime: 30 * 1000,
  });

export const useCharterAdminWaitlist = () =>
  useQuery({
    queryKey: [KEY, 'admin', 'waitlist'],
    queryFn: () => adminApi.listWaitlist(),
    staleTime: 60 * 1000,
  });
